// storage.js — Abstract storage layer for AT Tools platform
//
// Public interface:
//   storage.save(collection, id, data)   → void
//   storage.load(collection, id)         → { ...data, _meta } | null
//   storage.list(collection)             → Array<{ id, meta }> sorted by updatedAt desc
//   storage.delete(collection, id)       → void
//   storage.exportJSON(collection, id)   → triggers browser download of .json file
//   storage.importJSON(file)             → Promise<{ collection, id, data }>
//
// Backend: localStorage with keys "at-tools:{collection}:{id}"
//
// Para migrar a Cloudflare D1: reemplazar localStorageBackend por d1Backend con la misma interfaz.

const PREFIX = "at-tools";

function makeKey(collection, id) {
  return `${PREFIX}:${collection}:${id}`;
}

// ─── localStorage backend ──────────────────────────────────────────────────────

const localStorageBackend = {
  save(collection, id, data) {
    try {
      const key = makeKey(collection, id);
      const existing = localStorageBackend.load(collection, id);
      const now = new Date().toISOString();
      const meta = {
        createdAt: existing?._meta?.createdAt ?? now,
        updatedAt: now,
      };
      const payload = { ...data, _meta: meta };
      localStorage.setItem(key, JSON.stringify(payload));
    } catch (e) {
      console.error("[storage] save failed:", e);
    }
  },

  load(collection, id) {
    try {
      const raw = localStorage.getItem(makeKey(collection, id));
      if (raw === null) return null;
      return JSON.parse(raw);
    } catch (e) {
      console.error("[storage] load failed:", e);
      return null;
    }
  },

  list(collection) {
    try {
      const keyPrefix = `${PREFIX}:${collection}:`;
      const results = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key || !key.startsWith(keyPrefix)) continue;
        const id = key.slice(keyPrefix.length);
        try {
          const parsed = JSON.parse(localStorage.getItem(key));
          results.push({ id, meta: parsed?._meta ?? {} });
        } catch {
          // skip malformed entries
        }
      }
      results.sort((a, b) => {
        const ta = a.meta.updatedAt ?? "";
        const tb = b.meta.updatedAt ?? "";
        return tb.localeCompare(ta);
      });
      return results;
    } catch (e) {
      console.error("[storage] list failed:", e);
      return [];
    }
  },

  delete(collection, id) {
    try {
      localStorage.removeItem(makeKey(collection, id));
    } catch (e) {
      console.error("[storage] delete failed:", e);
    }
  },

  exportJSON(collection, id) {
    try {
      const raw = localStorageBackend.load(collection, id);
      if (raw === null) {
        console.error("[storage] exportJSON: entry not found", collection, id);
        return;
      }
      const { _meta, ...data } = raw;
      const payload = {
        collection,
        id,
        data,
        exportedAt: new Date().toISOString(),
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const date = new Date().toISOString().slice(0, 10);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${collection}-${id}-${date}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error("[storage] exportJSON failed:", e);
    }
  },

  importJSON(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const parsed = JSON.parse(event.target.result);
          if (
            typeof parsed !== "object" ||
            parsed === null ||
            typeof parsed.collection !== "string" ||
            typeof parsed.id !== "string" ||
            typeof parsed.data !== "object" ||
            parsed.data === null
          ) {
            reject(
              new Error(
                "[storage] importJSON: invalid file structure — expected { collection, id, data }"
              )
            );
            return;
          }
          resolve({
            collection: parsed.collection,
            id: parsed.id,
            data: parsed.data,
          });
        } catch (e) {
          reject(new Error("[storage] importJSON: failed to parse JSON — " + e.message));
        }
      };
      reader.onerror = () =>
        reject(new Error("[storage] importJSON: file read error"));
      reader.readAsText(file);
    });
  },
};

// ─── Active backend ────────────────────────────────────────────────────────────
// Para migrar a Cloudflare D1: reemplazar localStorageBackend por d1Backend con la misma interfaz.

const backend = localStorageBackend;

// ─── Public API ───────────────────────────────────────────────────────────────

const storage = {
  save: (collection, id, data) => backend.save(collection, id, data),
  load: (collection, id) => backend.load(collection, id),
  list: (collection) => backend.list(collection),
  delete: (collection, id) => backend.delete(collection, id),
  exportJSON: (collection, id) => backend.exportJSON(collection, id),
  importJSON: (file) => backend.importJSON(file),
};

export default storage;

// ─── Known collections ────────────────────────────────────────────────────────

export const COLLECTIONS = {
  ASSESSMENTS: "assessments",
  BREACH_REGISTER: "breach-register",
  DOCUMENTS: "documents",
};
